export type PrimaryModule = 'discover' | 'orders' | 'community' | 'service' | 'admin' | 'none';

export type ServiceWorkspaceActiveSection =
  | 'orders'
  | 'planner'
  | 'public_profile'
  | 'service_profile'
  | 'settings'
  | 'product_feedback'
  | 'community';

type ResolvePrimaryModuleParams = {
  pathname: string;
  search?: string;
  currentUserId?: string | null;
  canAccessServiceModule?: boolean;
};

type ResolveServiceWorkspaceSectionParams = {
  pathname: string;
  search?: string;
  isOwnerPreview?: boolean;
  allowCommunityWorkspace?: boolean;
};

export const SERVICE_WORKSPACE_SECTION_LABELS: Record<ServiceWorkspaceActiveSection, string> = {
  orders: 'Zlecenia',
  planner: 'Planer',
  public_profile: 'Widok publiczny',
  service_profile: 'Dane serwisu',
  settings: 'Ustawienia',
  product_feedback: 'Moje zgłoszenia',
  community: 'Forum',
};

const DISCOVERY_ROUTES = new Set(['/app', '/app/discover', '/app/list', '/map', '/list']);

const normalizePathname = (pathname: string): string => {
  const trimmed = String(pathname || '').trim();
  if (!trimmed) {
    return '/';
  }
  if (trimmed === '/') {
    return '/';
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

const getServiceProviderIdFromPath = (pathname: string): string | null => {
  const match = normalizePathname(pathname).match(/^\/service-provider\/([^/]+)$/i);
  return match?.[1] || null;
};

const getWorkspaceReturnTarget = (search: string): string | null => {
  const rawTarget = String(new URLSearchParams(search).get('returnTo') || '').trim();
  if (!rawTarget.startsWith('/')) {
    return null;
  }

  const [pathOnly] = rawTarget.split(/[?#]/u, 1);
  return normalizePathname(pathOnly || rawTarget);
};

const isServiceWorkspaceReturnTarget = (target: string | null): boolean =>
  target === '/app/service' ||
  target === '/app/service/planner' ||
  target === '/app/dashboard' ||
  target === '/app/service-profile' ||
  target === '/app/service/equipment-types';

export const isServiceWorkspaceRequestDetail = ({
  pathname,
  search = '',
}: {
  pathname: string;
  search?: string;
}): boolean => {
  const normalizedPathname = normalizePathname(pathname);
  if (!normalizedPathname.startsWith('/app/request/')) {
    return false;
  }

  const params = new URLSearchParams(search);
  if (params.get('workspace') === 'service') {
    return true;
  }

  return isServiceWorkspaceReturnTarget(getWorkspaceReturnTarget(search));
};

export const resolvePrimaryModuleFromPathname = ({
  pathname,
  search = '',
  currentUserId,
  canAccessServiceModule = false,
}: ResolvePrimaryModuleParams): PrimaryModule => {
  const normalizedPathname = normalizePathname(pathname);
  const searchParams = new URLSearchParams(search);

  if (normalizedPathname.startsWith('/admin')) {
    return 'admin';
  }
  if (
    canAccessServiceModule &&
    isServiceWorkspaceRequestDetail({
      pathname: normalizedPathname,
      search,
    })
  ) {
    return 'service';
  }
  if (canAccessServiceModule && normalizedPathname === '/app/product-feedback') {
    return 'service';
  }
  if (normalizedPathname === '/app/dashboard' || normalizedPathname.startsWith('/app/service')) {
    return 'service';
  }
  if (normalizedPathname.startsWith('/app/my-requests')) {
    return 'orders';
  }
  if (normalizedPathname.startsWith('/app/community') || normalizedPathname === '/community') {
    return 'community';
  }
  if (
    normalizedPathname.startsWith('/app/settings') &&
    searchParams.get('workspace') === 'service'
  ) {
    return 'service';
  }
  if (DISCOVERY_ROUTES.has(normalizedPathname)) {
    return 'discover';
  }

  const serviceProviderId = getServiceProviderIdFromPath(normalizedPathname);
  if (
    serviceProviderId &&
    canAccessServiceModule &&
    currentUserId &&
    serviceProviderId === currentUserId
  ) {
    return 'service';
  }

  return 'none';
};

export const resolveServiceWorkspaceActiveSection = ({
  pathname,
  search = '',
  isOwnerPreview = false,
  allowCommunityWorkspace = false,
}: ResolveServiceWorkspaceSectionParams): ServiceWorkspaceActiveSection | null => {
  const normalizedPathname = normalizePathname(pathname);
  const params = new URLSearchParams(search);

  if (
    isServiceWorkspaceRequestDetail({
      pathname: normalizedPathname,
      search,
    })
  ) {
    return 'orders';
  }
  if (normalizedPathname.startsWith('/app/service/equipment-types')) {
    return 'service_profile';
  }
  if (normalizedPathname === '/app/service/planner') {
    return 'planner';
  }
  if (normalizedPathname.startsWith('/app/service-profile')) {
    return 'service_profile';
  }
  if (normalizedPathname === '/app/dashboard' || normalizedPathname.startsWith('/app/service')) {
    return 'orders';
  }
  if (normalizedPathname.startsWith('/app/settings')) {
    if (params.get('workspace') === 'service') {
      return 'settings';
    }
  }
  if (normalizedPathname === '/app/product-feedback') {
    return 'product_feedback';
  }
  if (allowCommunityWorkspace && normalizedPathname.startsWith('/app/community')) {
    if (params.get('workspace') === 'service') {
      return 'community';
    }
  }
  if (isOwnerPreview && normalizedPathname.startsWith('/service-provider/')) {
    return 'public_profile';
  }

  return null;
};
